// Tlačítka ← → v hlavičce = krok zpět / vpřed v historii aplikace (rozhodnutí zadavatele 12. 10. 2026).
// Neaktivní tlačítko tu popisuje STAV (není kam jít), ne právo — krok v historii smí každý. Dostupnost kroku se bere
// z `window.history.state.idx` (react-router ho píše ke každému záznamu), kde není (paměťová historie v testech),
// z vlastního počítadla podle typu navigace. Nejvyšší dosažený index se pamatuje v sessionStorage, ať vpřed
// funguje i po obnovení stránky. Okna otevřená odkazem se dál zavírají přes `useZavritZpet` — ta jen volá
// `navigate(-1)`, což s těmito tlačítky nesouvisí.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import './hlavicka-navigace.css';

const KLIC_MAX = 'ecoser-historie-max-idx';

export const DUVOD_NENI_ZPET = 'Není kam zpět';
export const DUVOD_NENI_VPRED = 'Není kam vpřed';

function indexProhlizece(): number | null {
  if (typeof window === 'undefined') return null;
  const stav: unknown = window.history?.state;
  const idx = stav && typeof stav === 'object' ? (stav as { idx?: unknown }).idx : undefined;
  return typeof idx === 'number' && Number.isFinite(idx) ? idx : null;
}

function nactiMax(): number {
  try {
    const n = Number(window.sessionStorage.getItem(KLIC_MAX));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function ulozMax(n: number): void {
  try {
    window.sessionStorage.setItem(KLIC_MAX, String(n));
  } catch {
    // Bez úložiště se po obnovení stránky vpřed nezná — jen neaktivní tlačítko.
  }
}

/** Je v historii aplikace kam zpět a kam vpřed? */
export function useDostupnostHistorie(): { zpet: boolean; vpred: boolean } {
  const umisteni = useLocation();
  const typ = useNavigationType();
  const stavRef = useRef<{ idx: number; max: number; mapa: Map<string, number> } | null>(null);
  const [dostupnost, setDostupnost] = useState({ zpet: false, vpred: false });

  useEffect(() => {
    const prohlizec = indexProhlizece();
    let s = stavRef.current;
    if (!s) {
      const idx = prohlizec ?? 0;
      s = { idx, max: prohlizec === null ? idx : Math.max(idx, nactiMax()), mapa: new Map() };
      stavRef.current = s;
    } else if (typ === 'PUSH') {
      s.idx = prohlizec ?? s.idx + 1;
      s.max = s.idx;
    } else if (typ === 'POP') {
      s.idx = prohlizec ?? s.mapa.get(umisteni.key) ?? s.idx;
      s.max = Math.max(s.max, s.idx);
    } else if (prohlizec !== null) {
      s.idx = prohlizec;
    }
    s.mapa.set(umisteni.key, s.idx);
    if (prohlizec !== null) ulozMax(s.max);
    setDostupnost({ zpet: s.idx > 0, vpred: s.idx < s.max });
  }, [umisteni.key, typ]);

  return dostupnost;
}

function Sipka({ smer }: { smer: 'zpet' | 'vpred' }) {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={smer === 'zpet' ? 'M20 12H5M11 6l-6 6 6 6' : 'M4 12h15M13 6l6 6-6 6'}
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function HistorieTlacitka() {
  const navigate = useNavigate();
  const { zpet, vpred } = useDostupnostHistorie();
  return (
    <div className="hlavicka__historie" role="group" aria-label="Historie stránek">
      <button
        type="button"
        className="hlavicka__historie-tlacitko hlavicka__historie-tlacitko--zpet"
        disabled={!zpet}
        title={zpet ? 'Zpět' : DUVOD_NENI_ZPET}
        aria-label={zpet ? 'Zpět' : `Zpět — ${DUVOD_NENI_ZPET.toLowerCase()}`}
        onClick={() => void navigate(-1)}
      >
        <Sipka smer="zpet" />
      </button>
      <button
        type="button"
        className="hlavicka__historie-tlacitko hlavicka__historie-tlacitko--vpred"
        disabled={!vpred}
        title={vpred ? 'Vpřed' : DUVOD_NENI_VPRED}
        aria-label={vpred ? 'Vpřed' : `Vpřed — ${DUVOD_NENI_VPRED.toLowerCase()}`}
        onClick={() => void navigate(1)}
      >
        <Sipka smer="vpred" />
      </button>
    </div>
  );
}
